import type { ReactNode } from "react";
import { Link } from "wouter";
import { ArrowLeft } from "lucide-react";
import { cn } from "../lib/cn";
import { ACCENTS, type AccentName } from "../lib/accents";
import { FOCUS_RING } from "../lib/focusRing";
import type { ClayArtName } from "../lib/clayArt";
import { ClayArt } from "./ClayArt";
import { Sparkle, Star } from "./decor";

export interface PlayfulHeroProps {
  title: ReactNode;
  eyebrow?: string;
  description?: ReactNode;
  /** Calls to action, set under the description (as on the landing hero). */
  actions?: ReactNode;
  /** Small back link rendered above the title. */
  back?: { href: string; label: string };
  /** Hue of the eyebrow, sparkles and the glow behind the art. Default `sky`. */
  accent?: AccentName;
  /**
   * Scale: `full` (a page's front door — the dashboard, community, stories:
   * the bigger title and, by default, a clay illustration) or `compact`
   * (sub-pages). `none` also drops the sparkles. Default `full`.
   */
  decor?: "full" | "compact" | "none";
  /**
   * Clay illustration on the right (lib/clayArt.ts), or `false` for none.
   * Defaults to a family picture on `full` heroes and none on `compact`.
   */
  art?: ClayArtName | false;
  className?: string;
}

// What a `full` hero shows when the page names no art.
const FULL_DEFAULT_ART: Record<AccentName, ClayArtName> = {
  sky: "family",
  rose: "hug",
  amber: "familyWave",
  emerald: "familyWave",
  violet: "hug",
  indigo: "family",
};

// Art width per scale, kept under each file's sharp size (literal classes).
const ART_SIZE: Record<"full" | "compact", Record<ClayArtName, string>> = {
  full: {
    family: "w-28 sm:w-44 lg:w-56",
    familyWave: "w-32 sm:w-52 lg:w-64",
    hug: "w-28 sm:w-40 lg:w-48",
    momList: "w-24 sm:w-36 lg:w-44",
    list: "w-24 sm:w-32 lg:w-36",
    controls: "w-24 sm:w-32 lg:w-36",
    send: "w-24 sm:w-32 lg:w-36",
    lock: "w-24 sm:w-36 lg:w-44",
    heart: "w-24 sm:w-32 lg:w-36",
  },
  compact: {
    family: "w-20 sm:w-32 lg:w-36",
    familyWave: "w-24 sm:w-36 lg:w-44",
    hug: "w-20 sm:w-28 lg:w-32",
    momList: "w-[4.5rem] sm:w-28 lg:w-32",
    list: "w-20 sm:w-28 lg:w-32",
    controls: "w-20 sm:w-28 lg:w-32",
    send: "w-20 sm:w-28 lg:w-32",
    lock: "w-20 sm:w-28 lg:w-32",
    heart: "w-20 sm:w-28 lg:w-32",
  },
};

/**
 * The page hero for the logged-in screens, in the landing page's voice: an
 * open block on the page's scene (no box, no tinted band), an accent eyebrow,
 * a big friendly Fredoka title, a calm sub-line, actions underneath, and a
 * clay illustration floating on a soft glow. Sparkles are decorative,
 * aria-hidden and frozen under reduced motion (globals.css).
 *
 * On a phone the art sits beside the title and the description and actions
 * take the full width below, so nothing is squeezed into a narrow column.
 */
export function PlayfulHero({
  title,
  eyebrow,
  description,
  actions,
  back,
  accent = "sky",
  decor = "full",
  art,
  className,
}: PlayfulHeroProps) {
  const scale = decor === "full" ? "full" : "compact";
  const artName = art === false ? null : (art ?? (decor === "full" ? FULL_DEFAULT_ART[accent] : null));

  return (
    <div className={cn("relative", className)}>
      {back && (
        <Link
          href={back.href}
          className={cn(
            "mb-4 inline-flex items-center gap-1.5 rounded-full text-sm font-medium text-neutral-600 transition hover:text-neutral-900 sm:mb-6",
            FOCUS_RING,
          )}
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {back.label}
        </Link>
      )}

      {/* Phone: [title | art] then the body across both columns. From sm the
          art spans four rows and the copy sits in the middle two, so the copy
          is centred against the picture however tall either one is. */}
      <div className="relative grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 sm:grid-rows-[1fr_auto_auto_1fr] sm:gap-x-10">
        <div className="relative min-w-0 sm:col-start-1 sm:row-start-2">
          {eyebrow && (
            <p
              className={cn(
                "text-xs font-semibold uppercase tracking-wide sm:text-sm",
                ACCENTS[accent].text,
              )}
            >
              {eyebrow}
            </p>
          )}
          {/* Size and line height together (text-x/[1.1]): a bare leading-*
              would be dropped by tailwind-merge or reset by each breakpoint's
              text-* line height. */}
          <h1
            className={cn(
              "text-balance font-display font-bold tracking-tight text-neutral-900",
              eyebrow && "mt-2",
              scale === "full"
                ? "text-[1.85rem]/[1.12] sm:text-4xl/[1.1] lg:text-[2.75rem]/[1.08]"
                : "text-[1.6rem]/[1.15] sm:text-3xl/[1.12] lg:text-[2.1rem]/[1.1]",
            )}
          >
            {title}
          </h1>
        </div>

        {artName ? (
          <div className={cn("relative sm:col-start-2 sm:row-span-4 sm:row-start-1", ART_SIZE[scale][artName])}>
            <ClayArt name={artName} accent={accent} priority />
            {decor !== "none" && (
              <div aria-hidden="true" className="pointer-events-none absolute inset-0">
                <Sparkle className={cn("absolute -left-2 top-[8%] h-4 w-4 opacity-80 sm:-left-6 sm:h-5 sm:w-5", ACCENTS[accent].text)} />
                <Star className="absolute -right-1 bottom-[10%] h-3.5 w-3.5 text-amber-300 sm:-right-3 sm:h-4 sm:w-4" />
              </div>
            )}
          </div>
        ) : (
          decor !== "none" && (
            // No art: one sparkle in the far corner, clear of the copy.
            <Sparkle
              className={cn(
                "absolute right-1 top-0 hidden h-5 w-5 opacity-70 sm:block",
                ACCENTS[accent].text,
              )}
            />
          )
        )}

        {(description || actions) && (
          <div className="col-span-2 min-w-0 self-start sm:col-span-1 sm:col-start-1 sm:row-start-3">
            {description && (
              <p className="mt-3 max-w-2xl text-pretty text-[15px] leading-relaxed text-neutral-600 sm:mt-4 sm:text-lg">
                {description}
              </p>
            )}
            {actions && <div className="mt-5 flex flex-wrap items-center gap-2 sm:mt-6 sm:gap-3">{actions}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
